<script setup>
    import { computed, unref } from 'vue';
    import { useI18n } from 'vue-i18n';
    import useLiferayLocale from '../../../composables/useLiferayLocale';
    import getOptimizedImageUrl from '../../../helpers/getOptimizedImageUrl';
    import formatDisplayDate from '../../../helpers/formatDisplayDate';

    const { t } = useI18n();
    const { urlLocaleParameter } = useLiferayLocale();

    const props = defineProps({
        data: {
            type: Object,
            default: null,
        },

        isFeatured: {
            type: Boolean,
            default: false,
        },

        fallbackImage: {
            type: String,
            default:
                'https://look.ams-osram.com/transform/98089e6c-3697-4e87-b85a-a3e42ad9899d/ams-OSRAM-Texture-Assets?format=webp&io=transform:fill,width:1280',
        },
    });

    const categories = computed(() =>
        props.data.categories?.filter(category => category !== t('search_amsnewsandmedia_news')).join(', ')
    );
    const startDate = computed(() => formatDisplayDate(props.data.eventStartDate_iso8601));
    const endDate = computed(() => formatDisplayDate(props.data.eventEndDate_iso8601));
    const displayDate = computed(() => formatDisplayDate(props.data.displayDate_iso8601));
    const readMoreUrl = computed(() =>
        props.data.readMoreURL?.startsWith('http')
            ? props.data.readMoreURL
            : `${unref(urlLocaleParameter)}${props.data.readMoreURL}`
    );
    const imgSrc = computed(() =>
        props.isFeatured || !props.data?.imageUrl ? null : getOptimizedImageUrl(props.data.imageUrl, 400)
    );

    const eventPlace = computed(() => props.data?.eventPlace || null);

    const styles = computed(() => {
        if (!props.isFeatured) {
            return null;
        }

        return {
            'background-image': props.data.imageUrl ? `url(${props.data.imageUrl})` : `url(${props.fallbackImage})`,
        };
    });

    const classes = computed(() => [
        'news-card',
        {
            'is-featured': props.isFeatured,
        },
    ]);
</script>

<template>
    <a :class="classes" :href="readMoreUrl" :style="styles">
        <span v-if="categories.includes(t('newsHub_typeEvents'))" class="news-hero-flag">
            {{ t('newsHub_typeEvents') }}
            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 11 11" fill="none">
                <path d="M-0.5 7 V0H7L8.5 0Z" fill="#46555F" />
            </svg>
        </span>
        <img v-if="imgSrc" :src="imgSrc" class="news-card-image" />
        <img v-else-if="!isFeatured" :src="fallbackImage" class="news-card-image" aria-hidden="true" />
        <div class="news-card-info-container">
            <h3 class="title news-card-title" :class="{ 'is-2': isFeatured, 'is-3': !isFeatured }">
                {{ data.title }}
            </h3>
            <div class="news-card-meta">
                <span v-if="data.eventStartDate !== null" class="info-box__date">
                    <time class="news-card-meta-time" :datetime="data.eventStartDate">{{ startDate }}</time>
                </span>
                <span v-else-if="data.eventEndDate !== null">
                    <time class="news-card-meta-time" :datetime="data.eventEndDate">{{ endDate }}</time>
                </span>
                <span v-else class="info-box__date">
                    <time class="news-card-meta-time" :datetime="data.displayDate">{{ displayDate }}</time>
                </span>
                <span v-if="eventPlace" class="news-card-location"> {{ eventPlace }} </span>
                <div class="news-card-categories">
                    {{ categories }}
                </div>
            </div>
            <span class="news-card-actions">
                <span class="button is-secondary" :class="{ 'is-outlined': !isFeatured }">
                    {{ t('search_readMore') }}
                </span>
            </span>
        </div>
    </a>
</template>

<style lang="scss">
    .news-hero-flag {
        position: absolute;
        background-color: var(--color-bg-primary);
        color: hsl(var(--colors-base-white));
        padding: 0.5rem 1rem;
        font-size: 16px;
        font-weight: inherit;
        letter-spacing: -0.01em;
        right: -8.5px;
        line-height: 1.4;

        svg {
            right: -3px;
            position: absolute;
            width: 0.75rem;
            top: 100%;
        }
    }

    .news-card-categories {
        height: 3rem;
    }

    .news-card-event-date-location {
        margin-top: 1rem;
        display: flex;
        flex-direction: column;

        .info-box__date {
            display: flex;
            flex-direction: row;
            gap: 0.5rem;
        }
    }
</style>
